<script setup lang="ts">
// Floating "Ask Insiders" launcher. Mounted once by the default layout so agents can ask
// at every step of their work (Ask is no longer a nav tab). Verified agents get the global
// palette (same as Cmd+K); users who may use Ask but aren't phone-verified yet (free plan,
// entitled) go to the full /ask page, where the server runs the phone-verification prompt.

import { useAskPalette } from '~/composables/useAskPalette';

const { t } = useI18n();
const route = useRoute();
const { open } = useAskPalette();
const { isVerified, canSeeFreeFeatures } = useCanSeeFreeFeatures();

const isOnAskPage = computed(() => {
  const p = route.path || '';
  return p === '/ask' || p.startsWith('/ask/');
});

const onClick = () => {
  if (isVerified.value) {
    open();
    return;
  }
  navigateTo('/ask');
};
</script>

<template>
  <ClientOnly>
    <button
      v-if="canSeeFreeFeatures && !isOnAskPage"
      type="button"
      data-tour="nav-ask"
      class="ask-launcher group fixed bottom-[9.5rem] end-4 z-40 grid size-14 place-items-center rounded-full bg-gold-primary-120 text-black-100 shadow-[0_12px_32px_-8px_rgba(13,15,21,0.35)] transition-all hover:scale-105 hover:bg-gold-primary-100 hover:shadow-[0_16px_36px_-8px_rgba(13,15,21,0.45)] desktop:bottom-24"
      :aria-label="t('ask.launcher.ariaLabel')"
      @click="onClick"
    >
      <span class="font-mono text-[14px] font-bold tracking-[-0.02em]">{{ t('ask.common.aiInitials') }}</span>
      <!-- Tooltip on hover; hidden by default, fades in. -->
      <span
        class="ask-launcher-tip pointer-events-none absolute end-full top-1/2 me-3 hidden -translate-y-1/2 whitespace-nowrap rounded-md bg-black-100 px-2.5 py-1.5 font-mono text-[10.5px] uppercase tracking-[0.12em] text-white opacity-0 transition-opacity group-hover:opacity-100 tablet:block"
      >
        {{ t('ask.launcher.tooltip') }} <span class="ms-1 text-gold-primary-100">&#8984;K</span>
      </span>
    </button>
  </ClientOnly>
</template>

<style scoped lang="scss">
.ask-launcher {
  // Subtle drop-in when a page first paints.
  animation: ask-launcher-pop 0.4s cubic-bezier(0.2, 0.6, 0.2, 1) both;
}

@keyframes ask-launcher-pop {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.92);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
</style>
